import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { FieldOptionBase } from 'api/tasks';
import { Filter } from 'components/molecules/filters/types';
import { from_search } from 'components/pages/filter-transformer';
import { TemplateId } from 'data/templates/api';
import { queueMacrotask } from 'utils/macrotask';
import { FilterableField, SingleSelectField } from '../../tasks/fields';
import { SelectFilterOperationType } from '../../tasks/filters/select';
import { getTemplateIdFromFilter } from '../utils/common';

export interface TemplateItemInfo {
  value: TemplateId;
  label: string;
}

interface UseTemplateInsightFilterProps {
  filter: Filter;
  templateField: SingleSelectField;
  selectFilter: (next: Filter) => void;
  replaceFilter: (next: Filter) => void;
  template_lookup_by_name: Record<string, FilterableField>;
}

const predicateById = (key?: number) => (item: TemplateItemInfo) =>
  item.value === Number(key);

export const useTemplateInsightFilter = ({
  filter,
  templateField,
  selectFilter,
  replaceFilter,
  template_lookup_by_name,
}: UseTemplateInsightFilterProps): {
  updateTemplateInFilter: (value: number, shouldReplace?: boolean) => Filter;
  selection: {
    id?: number;
    data?: FieldOptionBase<number>;
  };
  savedId?: number;
  options: { ids: number[]; items: Array<TemplateItemInfo> };
} => {
  const updateTemplateInFilter = useCallback(
    (value: number, shouldReplace = false) => {
      const nextFilter: Filter = {
        ...filter,
        criteria: [
          ...filter.criteria.filter((x) => x.field.fieldName !== 'template'),
          {
            field: templateField,
            value: Number(value),
            type: SelectFilterOperationType.Is,
          },
        ],
      };

      if (shouldReplace) {
        queueMacrotask(() => replaceFilter(nextFilter));
      } else {
        selectFilter(nextFilter);
      }

      return nextFilter;
    },
    [filter, replaceFilter, selectFilter, templateField],
  );

  const selection = useMemo(() => {
    const id = getTemplateIdFromFilter(filter);

    if (!id) {
      return { id };
    }

    if (id === -1) {
      return { id, data: { label: 'All templates', value: -1 } };
    }

    const data = templateField?.metaData?.options.find(predicateById(id));

    return { id, data };
  }, [filter, templateField?.metaData?.options]);

  const options = useMemo(
    () => ({
      ids: templateField?.metaData?.options?.map(({ value }) => value) ?? [],
      items:
        sortBy(
          templateField?.metaData?.options?.map(({ value, label }) => ({
            value,
            label,
          })),
          'label',
        ) ?? [],
    }),
    [templateField?.metaData?.options],
  );

  const { search: queryParams } = useLocation();

  const savedId = useMemo(() => {
    const saved_filter = from_search(template_lookup_by_name)(queryParams);
    const id = getTemplateIdFromFilter(saved_filter);

    return id && options.ids.includes(id) ? id : undefined;
  }, [template_lookup_by_name, options.ids, queryParams]);

  return {
    updateTemplateInFilter,
    selection,
    savedId,
    options,
  };
};
